import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	type AllocationErrorData,
	AllocationMode,
	AllocationPriorityDimension,
	type AllocationResultData
} from "~/gql/graphqlBase.generated";

export interface StoreOverride {
	isExcluded?: boolean;
	weight?: number;
}

interface AllocationEngineState {
	allocationPriorities: AllocationPriorityDimension[];
	availableInventory: number;
	dialogOpen: boolean;
	equalizeWeeksOfSupply: boolean;
	errors: AllocationErrorData[] | null;
	favorHigherWeight: boolean;
	isCalculating: boolean;
	manualOverrides: Record<string, number>;
	mode: AllocationMode;
	reservationQuantity: number;
	results: AllocationResultData | null;
	selectedItemId: string | null;
	selectedItemName: string | null;
	selectedTemplateId: string | null;
	storeOverrides: Record<string, StoreOverride>;
	targetWeeksOfSupply: number | null;
}

const initialState: AllocationEngineState = {
	allocationPriorities: [],
	availableInventory: 0,
	dialogOpen: false,
	equalizeWeeksOfSupply: false,
	errors: null,
	favorHigherWeight: true,
	isCalculating: false,
	manualOverrides: {},
	mode: AllocationMode.AllocateAll,
	reservationQuantity: 0,
	results: null,
	selectedItemId: null,
	selectedItemName: null,
	selectedTemplateId: null,
	storeOverrides: {},
	targetWeeksOfSupply: null
};

export const allocationEngineSlice = createSlice({
	initialState,
	name: "allocationEngine",
	reducers: {
		applyTemplate: (
			state,
			action: PayloadAction<{
				allocationPriorities: AllocationPriorityDimension[];
				equalizeWeeksOfSupply: boolean;
				favorHigherWeight: boolean;
				mode: AllocationMode;
				targetWeeksOfSupply?: number | null;
			}>
		) => {
			state.allocationPriorities = action.payload.allocationPriorities;
			state.equalizeWeeksOfSupply = action.payload.equalizeWeeksOfSupply;
			state.favorHigherWeight = action.payload.favorHigherWeight;
			state.mode = action.payload.mode;
			state.targetWeeksOfSupply = action.payload.targetWeeksOfSupply ?? null;
		},
		clearManualOverrides: (state) => {
			state.manualOverrides = {};
		},
		clearResults: (state) => {
			state.results = null;
			state.errors = null;
			state.manualOverrides = {};
		},
		closeAllocationDialog: () => initialState,
		openAllocationDialog: (
			state,
			action: PayloadAction<{ availableInventory: number; itemId: string; itemName: string }>
		) => {
			state.dialogOpen = true;
			state.selectedItemId = action.payload.itemId;
			state.selectedItemName = action.payload.itemName;
			state.availableInventory = action.payload.availableInventory;
			state.results = null;
			state.errors = null;
			state.allocationPriorities = [];
			state.reservationQuantity = 0;
			state.storeOverrides = {};
			state.manualOverrides = {};
		},
		setAllocationPriorities: (state, action: PayloadAction<AllocationPriorityDimension[] | null>) => {
			state.allocationPriorities = action.payload ?? [];
		},
		setAvailableInventory: (state, action: PayloadAction<number | null>) => {
			state.availableInventory = action.payload ?? 0;
		},
		setEqualizeWeeksOfSupply: (state, action: PayloadAction<boolean | null>) => {
			state.equalizeWeeksOfSupply = action.payload ?? false;
		},
		setErrors: (state, action: PayloadAction<AllocationErrorData[] | null>) => {
			state.errors = action.payload;
		},
		setFavorHigherWeight: (state, action: PayloadAction<boolean | null>) => {
			state.favorHigherWeight = action.payload ?? true;
		},
		setIsCalculating: (state, action: PayloadAction<boolean | null>) => {
			state.isCalculating = action.payload ?? false;
		},
		setMode: (state, action: PayloadAction<AllocationMode | null>) => {
			state.mode = action.payload ?? AllocationMode.AllocateAll;
		},
		setReservationQuantity: (state, action: PayloadAction<number | null>) => {
			state.reservationQuantity = action.payload ?? 0;
		},
		setManualOverride: (state, action: PayloadAction<{ quantity: number; storeId: string }>) => {
			state.manualOverrides[action.payload.storeId] = action.payload.quantity;
		},
		setResults: (state, action: PayloadAction<AllocationResultData | null>) => {
			state.results = action.payload;
			state.manualOverrides = {};
		},
		setSelectedTemplateId: (state, action: PayloadAction<string | null>) => {
			state.selectedTemplateId = action.payload;
		},
		setStoreWeights: (state, action: PayloadAction<Record<string, number>>) => {
			for (const [storeId, weight] of Object.entries(action.payload)) {
				state.storeOverrides[storeId] = { ...state.storeOverrides[storeId], weight };
			}
		},
		setTargetWeeksOfSupply: (state, action: PayloadAction<number | null>) => {
			state.targetWeeksOfSupply = action.payload;
		},
		setItem: (state, action: PayloadAction<{ availableInventory: number; itemId: string; itemName: string }>) => {
			state.selectedItemId = action.payload.itemId;
			state.selectedItemName = action.payload.itemName;
			state.availableInventory = action.payload.availableInventory;
			state.storeOverrides = {};
			state.manualOverrides = {};
			state.results = null;
			state.errors = null;
		},
		toggleStoreExclusion: (state, action: PayloadAction<{ excluded: boolean; id: string }>) => {
			const { excluded, id } = action.payload;
			const existing = state.storeOverrides[id] ?? {};
			state.storeOverrides[id] = { ...existing, isExcluded: excluded };
		},
		updateStoreOverride: (state, action: PayloadAction<{ override: Partial<StoreOverride>; storeId: string }>) => {
			const { override, storeId } = action.payload;
			state.storeOverrides[storeId] = { ...state.storeOverrides[storeId], ...override };
		}
	}
});

export const {
	applyTemplate,
	clearManualOverrides,
	clearResults,
	closeAllocationDialog,
	openAllocationDialog,
	setAllocationPriorities,
	setAvailableInventory,
	setEqualizeWeeksOfSupply,
	setErrors,
	setFavorHigherWeight,
	setIsCalculating,
	setManualOverride,
	setMode,
	setReservationQuantity,
	setResults,
	setSelectedTemplateId,
	setStoreWeights,
	setTargetWeeksOfSupply,
	setItem,
	toggleStoreExclusion,
	updateStoreOverride
} = allocationEngineSlice.actions;

export default allocationEngineSlice.reducer;
